import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ApiService } from 'app/shared/api.service';

export interface PortfolioImage {
    id: number | string;
    url: string;
    name?: string;
}

export interface PortfolioItem {
    id: string;
    title: string;
    slug: string;
    description?: string | null;
    client_name: string;
    event_date: string;
    eventDateFormatted?: string;
    product_link: string;
    themed_product?: {
        id: string;
        name: string;
    } | null;
    tenant?: any;
    is_featured: boolean;
    is_active: boolean;
    sort_order?: number;
    images: PortfolioImage[];
    category?: string;
    coverImage?: string;
}

@Component({
    selector: 'app-portfolio',
    standalone: true,
    imports: [CommonModule, FormsModule, RouterModule],
    templateUrl: './portfolio.component.html',
    styleUrls: ['./portfolio.component.scss']
})
export class PortfolioComponent implements OnInit {

    isLoading: boolean = false;
    portfolios: PortfolioItem[] = [];
    filteredPortfolios: PortfolioItem[] = [];

    // Detail & Preview Modal state
    selectedPortfolio: PortfolioItem | null = null;
    isDetailModalOpen: boolean = false;
    previewMode: 'mobile' | 'desktop' | 'gallery' = 'mobile';
    activeGalleryImageIndex: number = 0;
    safePreviewUrl: SafeResourceUrl | null = null;

    constructor(
        private apiService: ApiService,
        private router: Router,
        private route: ActivatedRoute,
        private sanitizer: DomSanitizer,
        private cdr: ChangeDetectorRef
    ) { }

    ngOnInit(): void {
        if (typeof window !== 'undefined') {
            window.scrollTo({ top: 0, behavior: 'instant' });
        }

        this.fetchPortfolios();

        // Check for slug param in route
        this.route.paramMap.subscribe(params => {
            const slug = params.get('slug');
            if (slug) {
                this.loadPortfolioBySlug(slug);
            }
        });
    }

    async fetchPortfolios(): Promise<void> {
        this.isLoading = true;
        try {
            const apiEndpoints = [
                '/api/v1/front/public/portfolios/list?paginate=false',
                '/api/v1/front/public/portfolios'
            ];

            let rawList: any[] = [];
            for (const endpoint of apiEndpoints) {
                try {
                    let response: any = await this.apiService.get(endpoint, false);
                    if (!response || !response.success || !response.data) {
                        try {
                            const devRes = await fetch(`https://dev.api.visideastudio.com${endpoint}`);
                            if (devRes.ok) {
                                response = await devRes.json();
                            }
                        } catch (e) { }
                    }

                    if (!response || !response.success || !response.data) {
                        try {
                            const prodRes = await fetch(`https://api.visidea.id${endpoint}`, {
                                headers: { 'Accept': 'application/json' }
                            });
                            if (prodRes.ok) {
                                response = await prodRes.json();
                            }
                        } catch (e) { }
                    }

                    if (response && response.data) {
                        if (Array.isArray(response.data.list)) {
                            rawList = response.data.list;
                        } else if (Array.isArray(response.data)) {
                            rawList = response.data;
                        } else if (Array.isArray(response.data.data)) {
                            rawList = response.data.data;
                        }
                    } else if (Array.isArray(response)) {
                        rawList = response;
                    }

                    if (rawList && rawList.length > 0) {
                        break;
                    }
                } catch (e) { }
            }

            if (rawList && rawList.length > 0) {
                this.portfolios = rawList.map(item => this.mapApiPortfolioItem(item));
            } else {
                this.portfolios = [];
            }
        } catch (error) {
            console.error('Error fetching portfolios from API:', error);
            this.portfolios = [];
        } finally {
            this.isLoading = false;
            this.applyFilters();
            this.cdr.markForCheck();
        }
    }

    private mapApiPortfolioItem(item: any): PortfolioItem {
        let coverImg = '';
        if (item.cover_image_url) coverImg = item.cover_image_url;
        else if (item.image_url) coverImg = item.image_url;
        else if (typeof item.cover_image === 'string' && item.cover_image) coverImg = item.cover_image;
        else if (typeof item.image === 'string' && item.image) coverImg = item.image;
        else if (Array.isArray(item.images) && item.images.length > 0) {
            const first = item.images[0];
            coverImg = typeof first === 'string' ? first : (first?.url || first?.image_url || first?.path || '');
        } else if (Array.isArray(item.media) && item.media.length > 0) {
            const first = item.media[0];
            coverImg = first?.original_url || first?.url || '';
        }

        if (!coverImg) {
            coverImg = '/assets/visidea/banner_slide.jpg';
        }

        const titleLower = String(item.title || item.name || '').toLowerCase();
        const themeName = String(item.themed_product?.name || '').toLowerCase();

        let inferredCategory = 'wedding';
        if (titleLower.includes('game') || themeName.includes('game') || titleLower.includes('adventure') || titleLower.includes('pixel')) {
            inferredCategory = 'game';
        } else if (titleLower.includes('cinematic') || themeName.includes('cinematic') || titleLower.includes('chapel') || titleLower.includes('sumatra')) {
            inferredCategory = 'cinematic';
        } else if (titleLower.includes('thematic') || themeName.includes('thematic') || titleLower.includes('cinema') || titleLower.includes('love story')) {
            inferredCategory = 'thematic';
        } else if (titleLower.includes('event') || titleLower.includes('opening') || titleLower.includes('tedja') || titleLower.includes('birthday') || titleLower.includes('wisuda')) {
            inferredCategory = 'event';
        }

        const portfolio: PortfolioItem = {
            id: item.id || `portfolio-${Math.random().toString(36).substring(2, 7)}`,
            title: item.title || item.name || 'Web Undangan Digital',
            slug: item.slug || String(item.title || '').toLowerCase().replace(/[^a-z0-9]/g, '-'),
            description: item.description || null,
            client_name: item.client_name || item.title || 'Klien Visidea',
            event_date: item.event_date || '2026-10-07T00:00:00.000000Z',
            product_link: item.product_link || 'https://invitation.visideastudio.com',
            themed_product: item.themed_product || null,
            tenant: item.tenant || null,
            is_featured: Boolean(item.is_featured),
            is_active: item.is_active !== undefined ? Boolean(item.is_active) : true,
            sort_order: Number(item.sort_order) || 0,
            images: Array.isArray(item.images) ? item.images : [{ id: 1, url: coverImg, name: item.title }],
            category: inferredCategory,
            coverImage: coverImg
        };

        return this.formatPortfolioDates(portfolio);
    }

    private formatPortfolioDates(item: PortfolioItem): PortfolioItem {
        let dateFormatted = 'Segera Hadir';
        if (item.event_date) {
            try {
                const d = new Date(item.event_date);
                if (!isNaN(d.getTime())) {
                    dateFormatted = new Intl.DateTimeFormat('id-ID', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                    }).format(d);
                }
            } catch (e) { }
        }
        return {
            ...item,
            eventDateFormatted: dateFormatted
        };
    }

    applyFilters(): void {
        this.filteredPortfolios = [...this.portfolios];
        // Sort by sort_order / featured
        this.filteredPortfolios.sort((a, b) => {
            if (a.is_featured && !b.is_featured) return -1;
            if (!a.is_featured && b.is_featured) return 1;
            return (a.sort_order || 0) - (b.sort_order || 0);
        });
    }

    openLiveDemo(item: PortfolioItem, event?: Event): void {
        if (event) {
            event.stopPropagation();
        }
        if (item.product_link && typeof window !== 'undefined') {
            window.open(item.product_link, '_blank');
        }
    }

    openDetailModal(item: PortfolioItem): void {
        this.selectedPortfolio = item;
        this.activeGalleryImageIndex = 0;
        this.previewMode = 'mobile';
        this.updateSafePreviewUrl();
        this.isDetailModalOpen = true;
    }

    closeDetailModal(): void {
        this.isDetailModalOpen = false;
        this.selectedPortfolio = null;
        this.safePreviewUrl = null;
    }

    setPreviewMode(mode: 'mobile' | 'desktop' | 'gallery'): void {
        this.previewMode = mode;
        if (mode === 'mobile' || mode === 'desktop') {
            this.updateSafePreviewUrl();
        }
    }

    private updateSafePreviewUrl(): void {
        if (this.selectedPortfolio?.product_link) {
            this.safePreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.selectedPortfolio.product_link);
        } else {
            this.safePreviewUrl = null;
        }
    }

    async loadPortfolioBySlug(slug: string): Promise<void> {
        try {
            const apiEndpoint = `/api/v1/front/public/portfolios/${slug}`;
            let response: any = await this.apiService.get(apiEndpoint, false);

            if (!response || !response.success || !response.data) {
                try {
                    const devRes = await fetch(`https://dev.api.visideastudio.com${apiEndpoint}`);
                    if (devRes.ok) response = await devRes.json();
                } catch (e) { }
            }

            if (response && response.data) {
                const mapped = this.mapApiPortfolioItem(response.data);
                this.openDetailModal(mapped);
                return;
            }
        } catch (e) { }

        // Fallback search in local list
        const found = this.portfolios.find(p => p.slug.toLowerCase() === slug.toLowerCase());
        if (found) {
            this.openDetailModal(found);
        }
    }

    goToDigitalInvitationOrder(item?: PortfolioItem): void {
        if (this.isDetailModalOpen) {
            this.closeDetailModal();
        }
        if (item?.themed_product?.id) {
            this.router.navigate(['/digital-invitation'], {
                queryParams: { themeId: item.themed_product.id }
            });
        } else {
            this.router.navigate(['/digital-invitation']);
        }
    }

    consultWhatsApp(item?: PortfolioItem): void {
        const themeName = item?.themed_product?.name || item?.title || 'Undangan Digital';
        const msg = `Halo Visidea Studio, saya sangat tertarik dengan portofolio website *${item?.title || 'Undangan Digital'}* (Tema: ${themeName}). Saya ingin konsultasi untuk pembuatan website undangan serupa bagi acara saya. Mohon info paket dan penawarannya. Terima kasih!`;
        const waUrl = `https://wa.me/6282260084847?text=${encodeURIComponent(msg)}`;
        if (typeof window !== 'undefined') {
            window.open(waUrl, '_blank');
        }
    }
}
